import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, Users, Globe, CheckCircle, Target, TrendingUp, Leaf, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import CustomCursor from '../components/CustomCursor';
import { Head } from 'vite-react-ssg';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .op-w *, .op-w *::before, .op-w *::after {
    font-family: 'Outfit', sans-serif;
    box-sizing: border-box;
  }
  .op-w h1, .op-w h2, .op-w h3, .op-w h4 {
    font-family: 'Cormorant Garamond', serif;
  }

  .op-w {
    --navy:        #0A1535;
    --navy-mid:    #0D1D45;
    --blue:        #1B4FD8;
    --blue-mid:    #2563EB;
    --blue-bright: #3B7BFF;
    --gold:        #D4A017;
    --gold-light:  #F5D080;
    --ice:         #F0F5FF;
    --ice-mid:     #DBEAFE;
    --border:      #E2E8F0;
    --text:        #1E293B;
    --muted:       #64748B;
    --dim:         #94A3B8;
    --white:       #FFFFFF;
  }

  /* ══════════════════════════════════════
     HERO — Leadership banner style
  ══════════════════════════════════════ */
  .op-hero {
    background: #0f1e38;
    padding-bottom: 52px;
  }
  .op-hero-inner {
    max-width: 1152px; margin: 0 auto;
    padding: 0 24px;
    padding-top: 128px;
  }
  .op-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 0.6rem; font-weight: 500;
    letter-spacing: 0.3em; text-transform: uppercase;
    color: #8fafd4;
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 20px;
  }
  .op-eyebrow-dash {
    display: inline-block; width: 32px; height: 1px;
    background: rgb(59,130,246); flex-shrink: 0;
  }
  .op-hero-content {
    display: flex; align-items: flex-end; gap: 28px;
    margin-bottom: 16px;
  }
  .op-h1 {
    font-family: 'Cormorant Garamond', serif;
    font-weight: 500; letter-spacing: -0.02em;
    line-height: 1.1; color: #ffffff;
    font-size: clamp(36px, 5vw, 48px);
    margin: 0;
  }
  .op-h1-words {
    display: flex; flex-wrap: wrap; gap: 0 0.3em;
  }
  .op-h1 em {
    font-style: italic; font-weight: 400;
    color: rgb(59,130,246);
    display: block; margin-top: 4px;
  }
  .op-vdivider {
    flex-shrink: 0; width: 1px; align-self: stretch;
    background: linear-gradient(to bottom, transparent, rgba(59,130,246,0.5), transparent);
    margin-bottom: 4px;
  }
  .op-hero-desc {
    font-family: 'Outfit', sans-serif;
    font-size: 0.82rem;
    color: rgba(200,220,255,0.7);
    font-weight: 300; line-height: 1.75;
    max-width: 380px; margin-bottom: 4px;
  }
  .op-hero-rule {
    height: 1px;
    background: linear-gradient(to right, rgba(59,130,246,0.5), transparent);
    margin-top: 20px;
  }

  /* ══════════════════════════════════════
     SECTIONS WRAPPER
  ══════════════════════════════════════ */
  .op-sections {
    background: var(--white);
    position: relative; overflow: hidden;
    padding: 96px 0;
  }
  .op-sections-grid {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(59,123,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59,123,255,0.04) 1px, transparent 1px);
    background-size: 52px 52px;
  }
  .op-wash-a {
    position: absolute; top: -200px; right: -200px;
    width: 700px; height: 700px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,123,255,0.06) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }
  .op-wash-b {
    position: absolute; bottom: -150px; left: -150px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(212,160,23,0.05) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }
  .op-inner {
    max-width: 1280px; margin: 0 auto;
    padding: 0 64px; position: relative; z-index: 2;
  }

  /* ── SECTION BLOCK ── */
  .op-block {
    margin-bottom: 80px; padding-bottom: 80px;
    border-bottom: 1px solid;
    border-image: linear-gradient(90deg, transparent, var(--border) 20%, var(--border) 80%, transparent) 1;
  }
  .op-block:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }

  /* ── SECTION HEADER row ── */
  .op-block-header {
    display: flex; align-items: flex-start;
    gap: 28px; margin-bottom: 48px;
  }
  .op-block-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(64px, 8vw, 100px);
    font-weight: 600; font-style: italic;
    line-height: 0.85; letter-spacing: -0.04em;
    color: var(--ice-mid); flex-shrink: 0;
    user-select: none;
  }
  .op-block-title-group { flex: 1; padding-top: 8px; }
  .op-block-chapter {
    display: inline-flex; align-items: center; gap: 10px;
    margin-bottom: 10px;
    font-family: 'Outfit', sans-serif;
    font-size: 10px; font-weight: 600; letter-spacing: 0.28em; text-transform: uppercase;
    color: var(--gold);
  }
  .op-block-chapter-dash { width: 28px; height: 2px; background: var(--gold); }
  .op-block-h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px, 4vw, 56px);
    font-weight: 600; line-height: 1.04;
    color: var(--navy); margin: 0 0 6px;
    letter-spacing: -0.02em;
  }
  .op-block-h2 em { font-style: italic; font-weight: 500; color: var(--blue-bright); font-size: 0.8em; }
  .op-block-subtitle {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--dim);
    display: flex; align-items: center; gap: 10px;
  }
  .op-block-subtitle::before { content: ''; flex: 0 0 18px; height: 1px; background: var(--border); }

  /* ── BODY 2-COL ── */
  .op-block-body {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 56px; align-items: stretch;
  }

  /* LEFT — paragraphs + callout */
  .op-left { display: flex; flex-direction: column; }
  .op-p {
    font-family: 'Outfit', sans-serif;
    font-size: 16px; line-height: 1.82;
    color: var(--muted); margin-bottom: 16px;
  }
  .op-p strong { color: var(--navy); font-weight: 600; }

  .op-callout {
    margin-top: auto;
    background: var(--navy);
    padding: 28px 30px;
    border-left: 4px solid var(--gold);
    position: relative; overflow: hidden;
    box-shadow: 0 16px 48px rgba(10,21,53,0.12);
  }
  .op-callout::before {
    content: '"'; position: absolute; right: 14px; bottom: -20px;
    font-family: 'Cormorant Garamond', serif; font-size: 100px; font-weight: 600;
    color: rgba(255,255,255,0.05); line-height: 1; pointer-events: none;
  }
  .op-callout-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
  .op-callout-icon {
    width: 36px; height: 36px; flex-shrink: 0;
    background: rgba(212,160,23,0.15); border: 1px solid rgba(212,160,23,0.3);
    display: flex; align-items: center; justify-content: center;
  }
  .op-callout-title { font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 600; color: white; }
  .op-callout-text { font-family: 'Outfit', sans-serif; font-size: 13.5px; line-height: 1.76; color: rgba(255,255,255,0.70); }
  .op-callout-link {
    display: inline-flex; align-items: center; gap: 8px;
    margin-top: 14px;
    font-family: 'Outfit', sans-serif;
    font-size: 12px; font-weight: 600; letter-spacing: 0.06em;
    color: var(--gold-light);
    text-decoration: none;
    transition: color 0.2s ease;
  }
  .op-callout-link:hover { color: #ffffff; }
  .op-callout-link-arrow {
    display: flex; align-items: center; justify-content: center;
    width: 20px; height: 20px; border-radius: 50%;
    background: rgba(212,160,23,0.2); border: 1px solid rgba(212,160,23,0.4);
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .op-callout-link:hover .op-callout-link-arrow {
    background: rgba(212,160,23,0.4);
    transform: translateX(3px);
  }

  /* ── LINK CARD (replaces text in section 3 callout) ── */
  .op-callout-link-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;
    padding: 16px 18px;
    background: rgba(15, 23, 42, 0.88);
    border: 1px solid rgba(37, 99, 235, 0.45);
    text-decoration: none;
    position: relative;
    overflow: hidden;
    transition: background 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
  }
  .op-callout-link-card::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(37, 99, 235, 0.16));
    pointer-events: none;
  }
  .op-callout-link-card:hover {
    background: rgba(15, 23, 42, 1);
    border-color: rgba(37, 99, 235, 0.75);
    transform: translateX(4px);
  }
  .op-callout-link-card-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .op-callout-link-card-globe {
    width: 38px; height: 38px; flex-shrink: 0;
    background: rgba(37, 99, 235, 0.15);
    border: 1px solid rgba(37, 99, 235, 0.45);
    display: flex; align-items: center; justify-content: center;
  }
  .op-callout-link-card-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .op-callout-link-card-label {
    font-family: 'Outfit', sans-serif;
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.22em; text-transform: uppercase;
    color: rgba(168, 191, 255, 0.8);
  }
  .op-callout-link-card-url {
    font-family: 'Outfit', sans-serif;
    font-size: 14px; font-weight: 600;
    letter-spacing: 0.03em;
    color: #d4a017; /* keep gold accent for URL if needed */
  }
  .op-callout-link-card-arrow {
    display: flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; flex-shrink: 0;
    border: 1px solid rgba(37, 99, 235, 0.4);
    background: rgba(19, 7, 69, 0.97);
    transition: background 0.25s ease, transform 0.25s ease;
  }
  .op-callout-link-card:hover .op-callout-link-card-arrow {
    background: rgba(14, 24, 45, 0.3);
    transform: translateX(3px);
  }

  /* RIGHT — bullet card */
  .op-card {
    background: var(--ice);
    border: 1.5px solid var(--ice-mid);
    border-top: 4px solid var(--blue-mid);
    padding: 30px 28px;
    position: relative; overflow: hidden;
    display: flex; flex-direction: column;
    transition: box-shadow 0.3s ease;
  }
  .op-card::after {
    content: '';
    position: absolute; top: -28px; right: -28px;
    width: 88px; height: 88px; border-radius: 50%;
    background: radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%);
  }
  .op-card:hover { box-shadow: 0 16px 48px rgba(37,99,235,0.1); }

  .op-card-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 12px 4px 8px; border-radius: 100px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    margin-bottom: 16px;
    background: var(--ice-mid);
    border: 1px solid rgba(37,99,235,0.2);
    color: var(--blue-mid);
    width: fit-content;
  }
  .op-card-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px; font-weight: 600; line-height: 1.15;
    color: var(--navy); margin-bottom: 14px;
  }
  .op-card-divider {
    height: 1px; margin: 0 0 16px;
    background: linear-gradient(90deg, rgba(37,99,235,0.18), transparent);
    flex-shrink: 0;
  }
  .op-bullets {
    list-style: none; padding: 0; margin: 0 0 16px;
    display: flex; flex-direction: column; gap: 12px;
    flex: 1;
  }
  .op-bullets li {
    display: flex; align-items: flex-start; gap: 10px;
    font-family: 'Outfit', sans-serif;
    font-size: 13.5px; line-height: 1.72; color: var(--muted);
  }
  .op-bullet-dot {
    flex-shrink: 0; margin-top: 5px;
    width: 18px; height: 18px; border-radius: 50%;
    background: var(--ice-mid);
    border: 1px solid rgba(37,99,235,0.2);
    display: flex; align-items: center; justify-content: center;
  }
  .op-bullet-dot-inner {
    width: 5px; height: 5px; border-radius: 50%;
    background: var(--blue-mid);
  }
  .op-card-footer-divider {
    height: 1px; margin: 0 0 14px;
    background: linear-gradient(90deg, rgba(37,99,235,0.18), transparent);
    flex-shrink: 0;
  }
  .op-card-footer {
    display: flex; align-items: center; gap: 8px;
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.04em;
    color: var(--blue-mid); flex-shrink: 0;
  }

  /* ── CTA BUTTON ── */
  .op-cta {
    display: inline-flex; align-items: center; gap: 10px;
    margin-top: 20px;
    padding: 11px 20px;
    background: var(--navy);
    border: 1.5px solid var(--blue-mid);
    text-decoration: none;
    font-family: 'Outfit', sans-serif;
    font-size: 12px; font-weight: 600;
    letter-spacing: 0.06em; text-transform: uppercase;
    color: white;
    position: relative; z-index: 1;
    transition: background 0.25s ease, border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
    flex-shrink: 0; width: fit-content;
  }
  .op-cta:hover {
    background: var(--blue-mid);
    border-color: var(--blue-mid);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(37,99,235,0.25);
  }
  .op-cta-arrow {
    display: flex; align-items: center; justify-content: center;
    width: 22px; height: 22px;
    background: rgba(59,123,255,0.15);
    border: 1px solid rgba(59,123,255,0.3);
    border-radius: 50%;
    transition: background 0.25s ease, transform 0.25s ease;
  }
  .op-cta:hover .op-cta-arrow {
    background: rgba(255,255,255,0.2);
    transform: translateX(3px);
  }

  /* ── QBAR ── */
  .op-qbar {
    margin-top: 32px;
    padding: 18px 24px;
    background: var(--ice); border: 1px solid var(--ice-mid);
    border-left: 4px solid var(--blue-mid);
  }
  .op-qbar-text {
    font-family: 'Outfit', sans-serif;
    font-size: 14px; line-height: 1.74;
    color: var(--muted); font-style: italic; margin: 0;
  }
  .op-qbar-text strong { color: var(--navy); font-style: normal; font-weight: 600; }

  /* ── ANIMATIONS ── */
  .op-fade { opacity: 0; transform: translateY(24px); transition: opacity 0.78s ease, transform 0.78s ease; }
  .op-fade.on { opacity: 1; transform: none; }

  @media (max-width: 1100px) {
    .op-inner { padding: 0 28px; }
    .op-block-body { grid-template-columns: 1fr; gap: 32px; }
    .op-vdivider { display: none; }
    .op-hero-desc { display: none; }
  }
  @media (max-width: 640px) {
    .op-sections { padding: 64px 0; }
    .op-h1 { font-size: clamp(30px, 8vw, 44px); }
    .op-block-header { flex-direction: column; gap: 12px; }
    .op-block-num { font-size: 56px; }
    .op-hero-inner { padding-top: 100px; }
  }
`;

// ── Section data ──────────────────────────────────────────────────────────────
const sections = [
  {
    num: '01',
    chapterTag: 'Section I',
    chapterIcon: <Briefcase size={11} color="#D4A017" />,
    title: <>Why <em>Entrepreneurship</em>?</>,
    subtitle: 'Build · Grow · Lead',
    body: [
      'IndusViva provides a pathway to entrepreneurship by enabling individuals to build their own business with minimal barriers. The focus is on <strong>skill development</strong>, consistent effort, and value creation, supported by a structured mentorship model.',
      'This approach ensures that entrepreneurship is <strong>responsible, ethical, and growth-oriented</strong>, rather than driven by short-term gains.',
    ],
    calloutIcon: <Target size={17} color="#D4A017" />,
    calloutTitle: 'Responsible Entrepreneurship',
    calloutText: 'True entrepreneurship is built on competence and ethics — IndusViva ensures that every individual grows with purpose, guided by the "I Do – We Do – You Do" mentorship framework.',
    cardBadge: 'Key Focus Areas',
    cardHeading: 'What You Develop',
    bullets: [
      'Leadership and business skills through structured training',
      'Sustainable income driven by genuine product-based growth',
      'Progressive independence via "I Do – We Do – You Do" learning',
    ],
    cardFooter: 'Ethical & Growth-Oriented',
    qbar: <>IndusViva's entrepreneurship model is built on <strong>skills first, income second</strong> — ensuring that every individual who joins grows with purpose and clarity.</>,
  },
  {
    num: '02',
    chapterTag: 'Section II',
    chapterIcon: <Users size={11} color="#D4A017" />,
    title: <> Why  <em>Community Commerce</em> ?</>,
    subtitle: 'Trust · Relationships · Shared Value',
    body: [
      'At its core, IndusViva follows a <strong>community-driven approach</strong> to commerce, where growth is built through trust, strong relationships, and shared progress.',
      'Rather than focusing only on transactions, the model encourages <strong>long-term engagement</strong>, where individuals grow together by creating meaningful value and supporting each other\'s journey.',
    ],
    calloutIcon: <TrendingUp size={17} color="#D4A017" />,
    calloutTitle: 'Value Over Transactions',
    calloutText: 'Community commerce means growth is never extracted from others — it is built together through genuine engagement, mutual support, and shared commitment to customer value.',
    cardBadge: 'How It Works',
    cardHeading: 'The Commerce Model',
    bullets: [
      'Customer-centric engagement at every touchpoint',
      'Collaboration and mutual growth within the network',
      'A supportive community that learns and grows together',
    ],
    cardFooter: 'Collective Success',
    qbar: <>Community commerce means that <strong>everyone grows together</strong> — success is not extracted from others but built collectively through genuine value and trust.</>,
  },
  {
    num: '03',
    chapterTag: 'Section III',
    chapterIcon: <Globe size={11} color="#D4A017" />,
    title: <> Why <em>VibrantViva</em> ?</>,
    subtitle: 'Wellness · Opportunity · Ethics',
    body: [
      'VibrantViva promotes IndusViva Community Commerce opportunities.With carefully selected ingredients and a structured approach to product development, IndusViva aims to provide solutions that complement daily routines and promote overall wellness. Our product range is designed to support areas such as energy, nutrition, and digestive balance.',
      'At IndusViva, we continue to focus on delivering products that align with evolving consumer needs, ensuring reliability, quality, and long-term value.',
    ],
    calloutIcon: <Globe size={17} color="#D4A017" />,
    calloutTitle: 'Visit IndusViva',
    calloutText: null,
    calloutLink: 'https://www.indusviva.com',
    calloutLinkLabel: 'www.indusviva.com',
    cardBadge: 'Key Highlights',
    cardHeading: 'Platform Pillars',
    bullets: [
      'High-quality wellness products at the core of the model',
      'Commitment to ethical and compliant business practices',
      'Green Planet Initiative and GIFT certification programs',
    ],
    qbar: <>True growth must go hand-in-hand with <strong>environmental stewardship and ethical responsibility</strong> — IndusViva's vision creates lasting value for people and the planet.</>,
  },
];

// ── Reusable block ────────────────────────────────────────────────────────────
const OpBlock = ({ section }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold: 0.06 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="op-block" ref={ref}>

<Head>
  {/* ── Basic Meta ── */}
  <title>VibrantViva-IndusViva Opportunity | Build Skills, Growth and Success</title>
  <meta
    name="description"
    content="Explore the VibrantViva/IndusViva opportunity to develop skills, achieve milestones, and grow within a structured environment focused on success."
  />
  <meta
    name="keywords"
    content="Entrepreneur Opportunity, Business Opportunity India, MLM Business Opportunity, Passive Income Opportunity, direct selling business opportunity, Entrepreneurship Development, Entrepreneur Success Platform, Success Opportunity, business opportunity for entrepreneurs, long term growth opportunity, VibrantViva Business Opportunity, Entrepreneurship, Entrepreneur Network, Career Growth, Income Opportunities, Small Business Ideas"
  />
  <meta name="author" content="VibrantViva" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://vibrantviva.com/opportunity" />

  {/* ── Open Graph (Facebook / WhatsApp / LinkedIn) ── */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://vibrantviva.com/opportunity" />
  <meta
    property="og:title"
    content="VibrantViva-IndusViva Opportunity | Build Skills, Growth & Success"
  />
  <meta
    property="og:description"
    content="Explore the VibrantViva/IndusViva opportunity to develop skills, achieve milestones, and grow within a structured environment focused on success."
  />
  <meta property="og:image" content="https://vibrantviva.com/assest/logo.png" />
  <meta property="og:image:width" content="200" />
  <meta property="og:image:height" content="200" />
  <meta property="og:site_name" content="VibrantViva" />
  <meta property="og:locale" content="en_IN" />

  {/* ── JSON-LD Structured Data ── */}
  <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": "https://vibrantviva.com/opportunity",
      "name": "VibrantViva-IndusViva Opportunity",
      "description":
        "Explore the VibrantViva/IndusViva opportunity to develop skills, achieve milestones, and grow within a structured environment focused on success.",
      "inLanguage": "en-IN",
      "isPartOf": {
        "@type": "WebSite",
        "name": "VibrantViva",
        "url": "https://vibrantviva.com/"
      },
      "publisher": {
        "@type": "Organization",
        "name": "VibrantViva",
        "url": "https://vibrantviva.com/",
        "logo": "https://vibrantviva.com/logo.png",
        "sameAs": [
          "https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957",
          "https://www.instagram.com/vibrantvivaofficial/",
          "https://www.linkedin.com/company/indusviva-healthsciences-private-limited/posts/?feedView=all&viewAsMember=true",
          "https://x.com/IndusvivaPvt?t=yf_P18W0HW6DR9ZbrhjRzg&s=09",
          "https://www.youtube.com/@VibrantViva"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-1800-103-4916",
          "contactType": "Customer Support",
          "hoursAvailable": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday"
              ],
              "opens": "09:30",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Saturday"],
              "opens": "09:30",
              "closes": "13:30"
            }
          ],
          "availableLanguage": [
            "English",
            "Hindi",
            "Kannada",
            "Tamil",
            "Telugu",
            "Malayalam"
          ],
          "areaServed": "IN",
          "contactOption": "TollFree"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
          "addressLocality": "Bangalore Urban",
          "addressRegion": "Karnataka",
          "postalCode": "560046",
          "addressCountry": "IN"
        },
        "location": {
          "@type": "Place",
          "name": "Viva Tower",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
            "addressLocality": "Bangalore Urban",
            "addressRegion": "Karnataka",
            "postalCode": "560046",
            "addressCountry": "IN"
          }
        }
      }
    })}
  }/>
</Head>

      {/* ── Section header ── */}
      <div className={`op-block-header op-fade ${vis ? 'on' : ''}`}>
        <div className="op-block-num">{section.num}</div>
        <div className="op-block-title-group">
          <p className="op-block-chapter">
            <span className="op-block-chapter-dash" />
            {section.chapterIcon}
            {section.chapterTag}
          </p>
          <h2 className="op-block-h2">{section.title}</h2>
          <p className="op-block-subtitle">{section.subtitle}</p>
        </div>
      </div>

      {/* ── Two-col body ── */}
      <div className="op-block-body">

        {/* LEFT */}
        <div className={`op-left op-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '100ms' }}>
          {section.body.map((p, i) => (
            <p className="op-p" key={i} dangerouslySetInnerHTML={{ __html: p }} />
          ))}
            {/* <div className="op-callout-head">
              <div className="op-callout-icon">{section.calloutIcon}</div>
              <span className="op-callout-title">{section.calloutTitle}</span>
            </div> */}

            {/* Body text — only shown when provided */}
            {section.calloutText && (
              <p className="op-callout-text">{section.calloutText}</p>
            )}

            {/* Rich link card — used when no calloutText (section 3) */}
            {!section.calloutText && section.calloutLink && (
              <a
                href={section.calloutLink}
                target="_blank"
                rel="noopener noreferrer"
                className="op-callout-link-card"
              >
                <div className="op-callout-link-card-left">
                  <div className="op-callout-link-card-globe">
                    <Globe size={16} color="#F5D080" />
                  </div>
                  <div className="op-callout-link-card-text">
                    <span className="op-callout-link-card-label">Official Website</span>
                    <span className="op-callout-link-card-url">{section.calloutLinkLabel}</span>
                  </div>
                </div>
                <div className="op-callout-link-card-arrow">
                  <ArrowRight size={14} color="#F5D080" />
                </div>
              </a>
            )}

            {/* Inline link — used when calloutText is also present */}
            {section.calloutText && section.calloutLink && (
              <a
                href={section.calloutLink}
                target="_blank"
                rel="noopener noreferrer"
                className="op-callout-link"
              >
                {section.calloutLinkLabel}
                <span className="op-callout-link-arrow">
                  <ArrowRight size={11} color="#F5D080" />
                </span>
              </a>
            )}
        </div>

        {/* RIGHT */}
        <div className={`op-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '200ms' }}>
          <div className="op-card">
            <div className="op-card-badge">
              <CheckCircle size={10} />
              {section.cardBadge}
            </div>
            <div className="op-card-heading">{section.cardHeading}</div>
            <div className="op-card-divider" />
            <ul className="op-bullets">
              {section.bullets.map((b, i) => (
                <li key={i}>
                  <span className="op-bullet-dot">
                    <span className="op-bullet-dot-inner" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            {section.ctaLink && (
              <a
                href={section.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="op-cta"
              >
                {section.ctaLabel}
                <span className="op-cta-arrow">
                  <ArrowRight size={12} color="white" />
                </span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Qbar ── */}
      <div className={`op-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '300ms' }}>
        <div className="op-qbar">
          <p className="op-qbar-text">{section.qbar}</p>
        </div>
      </div>

    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const Opportunity = () => {
  const heroRef = useRef(null);
  const [heroVis, setHeroVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setHeroVis(true); },
      { threshold: 0.1 }
    );
    if (heroRef.current) obs.observe(heroRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="op-w">
        <CustomCursor />

        {/* ── HERO — Leadership banner style ── */}
        <div className="op-hero">
          <Header />

          <div className="op-hero-inner" ref={heroRef}>
            <p className={`op-eyebrow op-fade ${heroVis ? 'on' : ''}`}>
              <span className="op-eyebrow-dash" />
              Growth &amp; Possibility
            </p>

            <div className={`op-hero-content op-fade ${heroVis ? 'on' : ''}`} style={{ transitionDelay: '80ms' }}>
              <h1 className="op-h1">
                <span className="op-h1-words">
                  <span>Discover</span><span>Your</span>
                </span>
                <em>Opportunity</em>
              </h1>

              <div className="op-vdivider" />

              <p className="op-hero-desc hidden md:block">
                Three pillars that define how we create sustainable success — for individuals, communities, and the brand.
              </p>
            </div>

            <div className={`op-hero-rule op-fade ${heroVis ? 'on' : ''}`} style={{ transitionDelay: '160ms' }} />
          </div>
        </div>

        {/* ── SECTIONS ── */}
        <div className="op-sections">
          <div className="op-sections-grid" />
          <div className="op-wash-a" />
          <div className="op-wash-b" />
          <div className="op-inner">
            {sections.map(s => <OpBlock key={s.num} section={s} />)}
          </div>
        </div>

        <Footer />
        <BackToTop />
      </div>
    </>
  );
};

export default Opportunity;